import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { RefinePanel } from "@/components/RefinePanel";
import { useLocale } from "@/context/LocaleContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton } from "@/components/common";
import { LanguageSelect } from "@/components/LanguageSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, ArrowRight, ArrowLeft, Check, ChevronUp, ChevronDown, Trash2, Plus,
  Copy, Save, Rocket, Loader2, Download,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const PRODUCTS = ["Kurser", "Konsulentydelser", "Software", "Abonnementer", "Produkter", "Events"];
const GOALS = ["Booke et møde", "Bestille en demo", "Kontakte virksomheden", "Tilmelde sig nyhedsbrevet", "Købe et produkt"];

function AutoTextarea({ value, onChange, className, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={1}
      className={`resize-none overflow-hidden ${className || ""}`}
      {...props}
    />
  );
}


function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${active ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/50"}`}>
      {children}
    </button>
  );
}

function valueColor(v) {
  return v === "Høj" || v === "Let" ? "text-foreground" : v === "Lav" || v === "Svær" ? "text-muted-foreground" : "text-muted-foreground";
}

export default function Lead() {
  const location = useLocation();
  const { company } = useAuth();
  const { t, contentLang } = useLocale();
  const [step, setStep] = useState("input1");
  const [productType, setProductType] = useState("");
  const [audience, setAudience] = useState("");
  const [challenge, setChallenge] = useState("");
  const [goal, setGoal] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [magnet, setMagnet] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [language, setLanguage] = useState(contentLang);

  const inputs = { product_type: productType, audience, challenge, goal };

  useEffect(() => {
    if (company?.audience && !audience) setAudience(company.audience);
    // eslint-disable-next-line
  }, [company]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.post("/lead/pdf", { magnet }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(magnet.title || "lead-magnet").replace(/\s+/g, "_").slice(0, 50)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); } finally { setPdfLoading(false); }
  };

  const findIdeas = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/lead/ideas", { product_type: productType, audience, challenge, goal, language });
      setIdeas(data.ideas);
      setStep("ideas");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const buildMagnet = async (ideaArg) => {
    const idea = ideaArg || selectedIdea;
    if (!idea) return;
    setSelectedIdea(idea);
    setLoading(true);
    setStep("building");
    try {
      const { data } = await api.post("/lead/magnet", { inputs, idea, language });
      setMagnet(data.magnet);
      setProjectId(data.project_id);
      setStep("magnet");
      toast.success(t("Lead magnet klar — gemt automatisk"));
    } catch (err) { toast.error(apiError(err)); setStep("ideas"); } finally { setLoading(false); }
  };

  // Direct build when arriving from Idébank with a chosen lead idea
  useEffect(() => {
    if (location.state?.leadIdea) {
      buildMagnet(location.state.leadIdea);
    }
    // eslint-disable-next-line
  }, []);

  const buildCampaign = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/lead/campaign", { magnet, inputs, language });
      setCampaign(data.campaign);
      setStep("campaign");
      toast.success("Hele kampagnen er klar");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const saveMagnet = async () => {
    try { await api.put(`/projects/${projectId}`, { title: magnet.title, content: magnet }); toast.success(t("Ændringer gemt")); }
    catch (err) { toast.error(apiError(err)); }
  };
  const duplicateMagnet = async () => {
    try { await api.post("/projects", { module: "lead", title: `${magnet.title} (kopi)`, content: magnet, topic: selectedIdea?.title || "" }); toast.success(t("Projekt duplikeret")); }
    catch (err) { toast.error(apiError(err)); }
  };

  const setField = (k, v) => setMagnet({ ...magnet, [k]: v });
  const setChapter = (i, k, v) => { const c = [...magnet.chapters]; c[i] = { ...c[i], [k]: v }; setMagnet({ ...magnet, chapters: c }); };
  const moveChapter = (i, d) => { const c = [...magnet.chapters]; const j = i + d; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; setMagnet({ ...magnet, chapters: c }); };
  const delChapter = (i) => setMagnet({ ...magnet, chapters: magnet.chapters.filter((_, x) => x !== i) });
  const addChapter = () => setMagnet({ ...magnet, chapters: [...magnet.chapters, { heading: "Nyt kapitel", body: "", tip: "", quote: "" }] });

  const reset = () => { setStep("input1"); setProductType(""); setAudience(""); setChallenge(""); setGoal(""); setIdeas([]); setSelectedIdea(null); setMagnet(null); setCampaign(null); setProjectId(null); };

  // -------- STEP: building loader --------
  if (step === "building") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
          className="h-12 w-12 rounded-xl bg-foreground flex items-center justify-center mb-6"><Sparkles className="h-6 w-6 text-background" /></motion.div>
        <h2 className="font-display text-2xl font-medium">{t("Bygger jeres lead magnet…")}</h2>
        <p className="text-muted-foreground mt-2 max-w-md">{t("Aicom AI skriver hele materialet — titel, kapitler, tips og call to action.")}</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader overline="Leadgenerator" title="Fra idé til komplet leadkampagne"
        subtitle="Aicom AI hjælper jer med at bygge en professionel lead magnet — og hele kampagnen omkring den." />

      {/* STEP 1: product */}
      {step === "input1" && (
        <Surface testid="lead-step-product">
          <h2 className="font-display text-xl font-medium mb-1">{t("Hvad ønsker I at sælge?")}</h2>
          <p className="text-sm text-muted-foreground mb-5">{t("Vælg det, der passer bedst — eller skriv jeres eget.")}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {PRODUCTS.map((p) => <Chip key={p} active={productType === p} onClick={() => setProductType(p)} testid={`lead-product-${p}`}>{t(p)}</Chip>)}
          </div>
          <Input value={PRODUCTS.includes(productType) ? "" : productType} onChange={(e) => setProductType(e.target.value)}
            placeholder={t("Eller skriv selv…")} className="max-w-sm" data-testid="lead-product-custom" />
          <div className="mt-6">
            <Button onClick={() => setStep("input2")} disabled={!productType.trim()} className="gap-2" data-testid="lead-next-1">{t("Næste")} <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </Surface>
      )}

      {/* STEP 2: audience/challenge/goal */}
      {step === "input2" && (
        <Surface testid="lead-step-details">
          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium">{t("Hvem er målgruppen?")}</label>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} className="mt-2" placeholder={t("Fx jeres typiske kunder")} data-testid="lead-audience" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("Hvad er målgruppens største udfordring?")}</label>
              <Textarea value={challenge} onChange={(e) => setChallenge(e.target.value)} rows={2} className="mt-2" placeholder={t("Fx den vigtigste udfordring jeres målgruppe står med")} data-testid="lead-challenge" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("Hvad ønsker I, at læseren skal gøre bagefter?")}</label>
              <div className="flex flex-wrap gap-2 mt-2 mb-2">
                {GOALS.map((g) => <Chip key={g} active={goal === g} onClick={() => setGoal(g)} testid={`lead-goal-${g}`}>{t(g)}</Chip>)}
              </div>
              <Input value={GOALS.includes(goal) ? "" : goal} onChange={(e) => setGoal(e.target.value)} placeholder={t("Eller skriv selv…")} className="max-w-sm" data-testid="lead-goal-custom" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("På hvilket sprog skal materialet være?")}</label>
              <div className="mt-2"><LanguageSelect value={language} onChange={setLanguage} testid="lead-language" /></div>
            </div>
          </div>
          <div className="mt-6 flex gap-2">
            <Button variant="outline" onClick={() => setStep("input1")} className="gap-2" data-testid="lead-back-2"><ArrowLeft className="h-4 w-4" /> {t("Tilbage")}</Button>
            <Button onClick={findIdeas} disabled={loading || !audience.trim() || !goal.trim()} className="gap-2" data-testid="lead-find-ideas">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("Find idéer")}
            </Button>
          </div>
        </Surface>
      )}

      {/* STEP 3: ideas */}
      {step === "ideas" && (
        <div data-testid="lead-step-ideas">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-medium">{t("Vælg den idé, vi skal bygge")}</h2>
            <Button variant="outline" size="sm" onClick={() => setStep("input2")} className="gap-2"><ArrowLeft className="h-3.5 w-3.5" /> {t("Tilbage")}</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ideas.map((idea, i) => {
              const sel = selectedIdea?.title === idea.title;
              return (
                <Surface key={i} delay={i * 0.03} testid={`lead-idea-${i}`}
                  className={`cursor-pointer ${sel ? "border-foreground" : ""}`}>
                  <div onClick={() => setSelectedIdea(idea)}>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="text-xs">{idea.type}</Badge>
                      {sel && <Check className="h-4 w-4 text-foreground" />}
                    </div>
                    <h3 className="font-display font-medium leading-snug">{idea.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{idea.why}</p>
                    <p className="text-xs text-muted-foreground mt-2"><b>{t("Rammer:")}</b> {idea.target}</p>
                    <div className="flex gap-4 mt-3 text-xs">
                      <span>{t("Leadværdi:")} <b className={valueColor(idea.lead_value)}>{idea.lead_value}</b></span>
                      <span>{t("Produktion:")} <b className={valueColor(idea.ease)}>{idea.ease}</b></span>
                    </div>
                  </div>
                  <Button size="sm" className="w-full mt-4 gap-2" data-testid={`lead-idea-generate-${i}`}
                    onClick={() => buildMagnet(idea)} disabled={loading}>
                    {loading && sel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {t("Generér lead magnet")}
                  </Button>
                </Surface>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 4: magnet document */}
      {step === "magnet" && magnet && (
        <div data-testid="lead-step-magnet">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Button onClick={downloadPdf} disabled={pdfLoading} className="gap-2" data-testid="lead-download-pdf">
              {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t("Download PDF")}
            </Button>
            <Button variant="outline" onClick={saveMagnet} className="gap-2" data-testid="lead-save"><Save className="h-4 w-4" /> {t("Gem ændringer")}</Button>
            <Button variant="outline" onClick={duplicateMagnet} className="gap-2" data-testid="lead-duplicate"><Copy className="h-4 w-4" /> {t("Duplikér")}</Button>
            <Button variant="ghost" onClick={reset} className="ml-auto gap-2" data-testid="lead-restart">{t("Start forfra")}</Button>
          </div>

          <div className="mb-4">
            <RefinePanel result={magnet} setResult={setMagnet} module="lead" projectId={projectId} language={language}
              quick={["Gør sproget enklere", "Mere konkret og handlingsorienteret", "Skarpere titel og intro", "Gør det kortere", "Tilføj flere konkrete eksempler"].map((q) => t(q))} />
          </div>

          {/* Professional document */}
          <div className="rounded-xl overflow-hidden border border-border" data-testid="lead-document">
            {/* Cover */}
            <div className="bg-slate-900 text-white p-10">
              <p className="text-xs uppercase tracking-[0.25em] text-indigo-300 mb-4">{t("Lead magnet")}</p>
              <AutoTextarea value={magnet.title || ""} onChange={(e) => setField("title", e.target.value)}
                className="bg-transparent text-3xl font-display font-medium w-full outline-none leading-tight" data-testid="lead-doc-title" />
              <AutoTextarea value={magnet.subtitle || ""} onChange={(e) => setField("subtitle", e.target.value)}
                className="bg-transparent text-slate-300 w-full outline-none mt-2 leading-snug" data-testid="lead-doc-subtitle" />
              <p className="text-indigo-300 text-sm mt-6">{magnet.cover_tagline}</p>
            </div>
            {/* Body on white paper */}
            <div className="bg-white text-slate-900 p-8 sm:p-10 space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-indigo-600 mb-2">{t("Introduktion")}</p>
                <Textarea value={magnet.introduction || ""} onChange={(e) => setField("introduction", e.target.value)}
                  rows={4} className="border-0 bg-transparent px-0 leading-relaxed resize-none focus-visible:ring-0 text-slate-700" data-testid="lead-doc-intro" />
              </div>

              {(magnet.chapters || []).map((ch, i) => (
                <div key={i} className="border-t border-slate-100 pt-5" data-testid={`lead-chapter-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-indigo-600 font-bold">{i + 1}.</span>
                    <input value={ch.heading || ""} onChange={(e) => setChapter(i, "heading", e.target.value)}
                      className="flex-1 bg-transparent text-lg font-display font-medium outline-none text-slate-900" data-testid={`lead-chapter-heading-${i}`} />
                    <button onClick={() => moveChapter(i, -1)} className="text-slate-400 hover:text-slate-900" data-testid={`lead-chapter-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
                    <button onClick={() => moveChapter(i, 1)} className="text-slate-400 hover:text-slate-900" data-testid={`lead-chapter-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
                    <button onClick={() => delChapter(i)} className="text-slate-400 hover:text-red-600" data-testid={`lead-chapter-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <Textarea value={ch.body || ""} onChange={(e) => setChapter(i, "body", e.target.value)}
                    rows={5} className="border-0 bg-transparent px-0 leading-relaxed resize-none focus-visible:ring-0 text-slate-700" data-testid={`lead-chapter-body-${i}`} />
                  {ch.quote ? <div className="border-l-2 border-indigo-500 bg-indigo-50 px-4 py-2 text-slate-600 italic text-sm rounded-r">{ch.quote}</div> : null}
                  {ch.tip ? <div className="bg-slate-100 px-4 py-2 rounded mt-2 text-sm text-slate-700">💡 <b>Tip:</b> {ch.tip}</div> : null}
                </div>
              ))}

              <button onClick={addChapter} className="text-sm text-indigo-600 flex items-center gap-1" data-testid="lead-add-chapter"><Plus className="h-4 w-4" /> {t("Tilføj kapitel")}</button>

              <div className="border-t border-slate-100 pt-5">
                <p className="text-xs uppercase tracking-[0.2em] text-indigo-600 mb-2">{t("Opsummering")}</p>
                <Textarea value={magnet.summary || ""} onChange={(e) => setField("summary", e.target.value)}
                  rows={4} className="border-0 bg-transparent px-0 leading-relaxed resize-none focus-visible:ring-0 text-slate-700" data-testid="lead-doc-summary" />
              </div>

              {/* CTA box */}
              <div className="bg-indigo-600 text-white rounded-lg p-6" data-testid="lead-doc-cta">
                <input value={magnet.cta?.heading || ""} onChange={(e) => setField("cta", { ...magnet.cta, heading: e.target.value })}
                  className="bg-transparent font-display text-lg font-medium w-full outline-none" data-testid="lead-cta-heading" />
                <Textarea value={magnet.cta?.text || ""} onChange={(e) => setField("cta", { ...magnet.cta, text: e.target.value })}
                  rows={2} className="border-0 bg-transparent px-0 resize-none focus-visible:ring-0 text-indigo-50 mt-1" data-testid="lead-cta-text" />
                <input value={magnet.cta?.button || ""} onChange={(e) => setField("cta", { ...magnet.cta, button: e.target.value })}
                  className="bg-white text-indigo-700 font-medium rounded px-4 py-1.5 mt-2 outline-none" data-testid="lead-cta-button" />
              </div>

              <Textarea value={magnet.contact || ""} onChange={(e) => setField("contact", e.target.value)}
                rows={2} className="border-0 bg-transparent px-0 resize-none focus-visible:ring-0 text-slate-500 text-sm" data-testid="lead-doc-contact" />
            </div>
          </div>

          {/* Campaign prompt */}
          {!campaign && (
            <Surface className="mt-6 text-center" testid="lead-campaign-prompt">
              <Rocket className="h-8 w-8 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-medium">{t("Vil du også have resten af kampagnen?")}</h3>
              <p className="text-sm text-muted-foreground mt-2 mb-4 max-w-lg mx-auto">{t("Aicom AI bygger landingpage, takkeside, LinkedIn-opslag, nyhedsbrev, Google & Meta Ads, CTA'er og en opfølgningsmail.")}</p>
              <Button onClick={buildCampaign} disabled={loading} className="gap-2" data-testid="lead-build-campaign">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("Ja, byg hele kampagnen")}
              </Button>
            </Surface>
          )}
        </div>
      )}

      {/* Campaign assets */}
      {campaign && step !== "input1" && <CampaignAssets campaign={campaign} />}
    </div>
  );
}

function Block({ title, children, text, testid }) {
  return (
    <Surface testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-medium">{title}</h3>
        {text != null && <CopyButton text={text} testid={`${testid}-copy`} label="" />}
      </div>
      {children}
    </Surface>
  );
}

function CampaignAssets({ campaign }) {
  const { t } = useLocale();
  const c = campaign;
  const lp = c.landingpage || {};
  const ty = c.thankyou || {};
  const nl = c.newsletter || {};
  const fm = c.followup_email || {};
  return (
    <div className="mt-8 space-y-4" data-testid="lead-campaign-assets">
      <h2 className="font-display text-2xl font-medium tracking-tight">{t("Din komplette kampagne")}</h2>

      <Block title={t("Landingpage")} text={`${lp.headline}\n${lp.subheadline}\n\n${lp.body}\n\n${(lp.bullets||[]).join("\n")}\n\n${lp.cta}`} testid="lead-lp">
        <p className="font-display text-lg">{lp.headline}</p>
        <p className="text-muted-foreground text-sm">{lp.subheadline}</p>
        <p className="text-sm mt-3 whitespace-pre-wrap">{lp.body}</p>
        <ul className="mt-3 space-y-1">{(lp.bullets || []).map((b, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><Check className="h-4 w-4 text-foreground shrink-0 mt-0.5" />{b}</li>)}</ul>
        <p className="text-sm mt-3 font-medium">{lp.cta}</p>
      </Block>

      <Block title={t("Takkeside")} text={`${ty.headline}\n\n${ty.body}\n\n${(ty.next_steps||[]).join("\n")}`} testid="lead-ty">
        <p className="font-display text-lg">{ty.headline}</p>
        <p className="text-sm mt-2 whitespace-pre-wrap">{ty.body}</p>
        <ul className="mt-3 space-y-1">{(ty.next_steps || []).map((s, i) => <li key={i} className="text-sm text-muted-foreground">→ {s}</li>)}</ul>
      </Block>

      <Block title={t("LinkedIn-opslag")} testid="lead-li">
        <div className="space-y-3">
          {(c.linkedin || []).map((p, i) => (
            <div key={i} className="p-3 rounded-lg bg-secondary/50">
              <div className="flex justify-between items-start gap-2"><p className="text-sm whitespace-pre-wrap flex-1">{p}</p><CopyButton text={p} testid={`lead-li-copy-${i}`} label="" /></div>
            </div>
          ))}
        </div>
      </Block>

      <Block title={t("Nyhedsbrev")} text={`${nl.subject}\n${nl.preview}\n\n${nl.headline}\n${nl.intro}\n\n${nl.body}\n\n${nl.cta}`} testid="lead-nl">
        <p className="text-sm"><b>{t("Emne:")}</b> {nl.subject}</p>
        <p className="text-sm text-muted-foreground"><b>{t("Preview:")}</b> {nl.preview}</p>
        <p className="font-display mt-3">{nl.headline}</p>
        <p className="text-sm mt-1">{nl.intro}</p>
        <p className="text-sm mt-2 whitespace-pre-wrap">{nl.body}</p>
        <p className="text-sm mt-2 font-medium">{nl.cta}</p>
      </Block>

      <Block title={t("Google Ads")} testid="lead-google">
        <div className="space-y-2">
          {(c.google_ads || []).map((a, i) => (
            <div key={i} className="p-3 rounded-lg bg-secondary/50 text-sm">
              <p className="font-medium">{typeof a === "string" ? a : a.headline}</p>
              {typeof a !== "string" && <p className="text-muted-foreground">{a.description}</p>}
            </div>
          ))}
        </div>
      </Block>

      <Block title={t("Meta Ads")} testid="lead-meta">
        <div className="space-y-2">{(c.meta_ads || []).map((a, i) => <div key={i} className="p-3 rounded-lg bg-secondary/50 text-sm whitespace-pre-wrap">{a}</div>)}</div>
      </Block>

      <Block title={t("Call To Actions")} text={(c.ctas || []).join("\n")} testid="lead-ctas">
        <div className="flex flex-wrap gap-2">{(c.ctas || []).map((t, i) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}</div>
      </Block>

      <Block title={t("Opfølgningsmail")} text={`${fm.subject}\n\n${fm.body}`} testid="lead-followup">
        <p className="text-sm"><b>{t("Emne:")}</b> {fm.subject}</p>
        <p className="text-sm mt-2 whitespace-pre-wrap">{fm.body}</p>
      </Block>
    </div>
  );
}
